import { MousePointerClickIcon } from "lucide-react";
import { ContextChip } from "./context-chip";
import type {
  PromptAddon,
  PromptAddonType,
} from "@/stores/slices/prompt-context.slice";
import { imagineStore } from "@/stores/imagine.store";

type SelectedElementChipProps = {
  addon: Extract<PromptAddon, { type: PromptAddonType.SELECTED_ELEMENT }>;
};

export function SelectedElementChip({ addon }: SelectedElementChipProps) {
  const { id, props } = addon;
  const removeAddon = imagineStore.use.promptContext.removeAddon();
  const sandboxClient = imagineStore.use.sandboxState.sandboxClient();

  const handleRemove = () => {
    // Deselect element in iframe
    if (sandboxClient && props.uniqueId) {
      sandboxClient.deselectElement(props.uniqueId);
    }
    // Remove from context
    removeAddon(id);
  };

  // Create a descriptive label for the element
  const getElementLabel = () => {
    const tagName = props.tagName || "element";

    // Priority 1: Show text content preview (most useful)
    if (props.previewText && props.previewText.trim()) {
      const truncated =
        props.previewText.length > 30
          ? props.previewText.slice(0, 30) + "..."
          : props.previewText;
      return `${tagName}: "${truncated}"`;
    }

    // Priority 2: Show element ID
    if (props.elementId && props.elementId.trim()) {
      const truncated =
        props.elementId.length > 20
          ? props.elementId.slice(0, 20) + "..."
          : props.elementId;
      return `${tagName}#${truncated}`;
    }

    // Priority 3: Show first class name
    if (props.className && props.className.trim()) {
      const firstClass = props.className.trim().split(/\s+/)[0];
      if (firstClass) {
        const truncated =
          firstClass.length > 20 ? firstClass.slice(0, 20) + "..." : firstClass;
        return `${tagName}.${truncated}`;
      }
    }

    // Fallback: Show xpath context (last two parts)
    if (props.xpath) {
      const parts = props.xpath.split("/").filter((p) => p);
      if (parts.length >= 2) {
        const lastTwo = parts
          .slice(-2)
          .map((p) => p.replace(/\[.*?\]/g, ""))
          .join(" > ");
        return lastTwo;
      }
    }

    // Final fallback: Just the tag name
    return tagName;
  };

  const label = getElementLabel();

  const icon = <MousePointerClickIcon className="size-4 text-primary" />;

  return (
    <ContextChip
      label={label}
      icon={icon}
      onRemove={props.removable ? handleRemove : undefined}
    />
  );
}
